import { useState, useCallback } from 'react';
import { useApi } from '@/hooks/useApi';
import { apiFetch } from '@/lib/api';
import {
  Wrench,
  Plus,
  Calendar,
  AlertTriangle,
  CheckCircle,
  DollarSign,
  Loader2,
  X,
} from 'lucide-react';

interface MaintenanceRecord {
  id: string;
  vehicle_id: string;
  vehicle_name: string;
  maintenance_type: string;
  due_date: string;
  status: 'scheduled' | 'upcoming' | 'overdue' | 'in_progress' | 'completed' | 'cancelled';
  vendor: string | null;
  cost: number | null;
  description: string | null;
  technician: string | null;
}

interface MaintenanceData {
  records: MaintenanceRecord[];
  scheduled: number;
  overdue: number;
  completedThisMonth: number;
  avgCost: number;
}

interface VehicleOption { id: string; name: string }

const STATUS_STYLES: Record<string, string> = {
  scheduled: 'bg-muted-foreground/10 text-muted-foreground border-border',
  upcoming: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
  overdue: 'bg-red-500/10 text-red-400 border-red-500/30',
  in_progress: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
  completed: 'bg-muted-foreground/5 text-muted-foreground border-border/30',
  cancelled: 'bg-muted-foreground/5 text-muted-foreground border-border/30',
};

const MAINTENANCE_TYPES = [
  'oil_change', 'tire_rotation', 'brake_service', 'transmission', 'engine',
  'electrical', 'body_work', 'inspection', 'recall', 'preventive', 'other',
] as const;

function isPast(dateStr: string): boolean {
  return new Date(dateStr) < new Date();
}

export default function MaintenancePage() {
  const { data, isLoading, refetch } = useApi<MaintenanceData>('/api/maintenance');
  const { data: vehiclesData } = useApi<{ vehicles: VehicleOption[] }>('/api/vehicles?pageSize=100');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const records = data?.records ?? [];
  const scheduled = data?.scheduled ?? 0;
  const overdue = data?.overdue ?? 0;
  const completedThisMonth = data?.completedThisMonth ?? 0;
  const avgCost = data?.avgCost ?? 0;
  const vehicles = vehiclesData?.vehicles ?? [];

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    setFormError('');
    const fd = new FormData(e.currentTarget);
    const selectedVehicle = vehicles.find(v => v.id === fd.get('vehicle_id'));
    try {
      await apiFetch('/api/maintenance', {
        method: 'POST',
        body: JSON.stringify({
          vehicle_id: fd.get('vehicle_id'),
          vehicle_name: selectedVehicle?.name ?? '',
          maintenance_type: fd.get('maintenance_type'),
          status: fd.get('status') || 'scheduled',
          description: fd.get('description') || undefined,
          vendor: fd.get('vendor') || undefined,
          technician: fd.get('technician') || undefined,
          cost: fd.get('cost') ? Number(fd.get('cost')) : undefined,
          mileage_at_service: fd.get('mileage_at_service') ? Number(fd.get('mileage_at_service')) : undefined,
          due_date: fd.get('due_date') || undefined,
          notes: fd.get('notes') || undefined,
        }),
      });
      setShowForm(false);
      refetch();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to schedule maintenance');
    } finally {
      setSaving(false);
    }
  }, [vehicles, refetch]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ background: 'rgba(16,185,129,0.1)' }}>
            <Wrench className="w-6 h-6" style={{ color: '#10B981' }} />
          </div>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--foreground)' }}>Maintenance</h1>
            <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Service schedules and maintenance tracking</p>
          </div>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-foreground rounded-lg transition-colors"
          style={{ background: '#10B981' }}
        >
          <Plus className="w-4 h-4" />
          Schedule Maintenance
        </button>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard icon={Calendar} label="Scheduled" value={scheduled} />
        <MetricCard icon={AlertTriangle} label="Overdue" value={overdue} warn />
        <MetricCard icon={CheckCircle} label="Completed This Month" value={completedThisMonth} accent />
        <MetricCard icon={DollarSign} label="Avg Cost" value={`$${avgCost.toLocaleString()}`} />
      </div>

      {/* Table */}
      <div className="rounded-xl overflow-hidden" style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}>
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--muted-foreground)' }} />
          </div>
        ) : records.length === 0 ? (
          <div className="text-center py-16" style={{ color: 'var(--muted-foreground)' }}>
            <Wrench className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No maintenance records</p>
            <p className="text-xs mt-1">Schedule your first maintenance service to get started.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b" style={{ borderColor: 'var(--glass-border)' }}>
                  {['Vehicle', 'Type', 'Due Date', 'Status', 'Vendor', 'Cost'].map(h => (
                    <th key={h} className="text-left text-xs font-medium uppercase tracking-wide px-4 py-3" style={{ color: 'var(--muted-foreground)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map((r) => (
                  <tr key={r.id} className="border-b hover:bg-white/5 transition-colors" style={{ borderColor: 'var(--glass-border)' }}>
                    <td className="px-4 py-3"><span className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>{r.vehicle_name || r.vehicleName}</span></td>
                    <td className="px-4 py-3"><span className="text-sm capitalize" style={{ color: 'var(--foreground)' }}>{(r.maintenance_type || r.type || '').replace(/_/g, ' ')}</span></td>
                    <td className="px-4 py-3">
                      <span className="text-sm" style={{ color: isPast(r.due_date || r.dueDate) && r.status !== 'completed' && r.status !== 'cancelled' ? '#EF4444' : 'var(--muted-foreground)' }}>
                        {(r.due_date || r.dueDate) ? new Date(r.due_date || r.dueDate).toLocaleDateString() : '--'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border capitalize ${STATUS_STYLES[r.status] ?? STATUS_STYLES.scheduled}`}>
                        {r.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3"><span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>{r.vendor ?? '--'}</span></td>
                    <td className="px-4 py-3 text-right"><span className="text-sm font-mono" style={{ color: 'var(--foreground)' }}>{r.cost != null ? `$${r.cost.toLocaleString()}` : '--'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Maintenance Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }}>
          <div className="w-full max-w-lg rounded-xl p-6 max-h-[90vh] overflow-y-auto"
            style={{ background: 'var(--surface-1)', border: '1px solid var(--border-subtle)' }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold" style={{ color: 'var(--foreground)' }}>Schedule Maintenance</h2>
              <button onClick={() => setShowForm(false)} className="p-1 rounded hover:bg-white/10">
                <X className="w-4 h-4" style={{ color: 'var(--muted-foreground)' }} />
              </button>
            </div>
            {formError && <div className="mb-4 p-3 rounded-lg text-sm" style={{ background: 'rgba(239,68,68,0.1)', color: '#EF4444' }}>{formError}</div>}
            <form onSubmit={handleSubmit} className="space-y-4">
              <FormField label="Vehicle" required>
                <select name="vehicle_id" required className="form-select">
                  <option value="">Select vehicle...</option>
                  {vehicles.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </FormField>
              <FormField label="Maintenance Type" required>
                <select name="maintenance_type" required className="form-select">
                  {MAINTENANCE_TYPES.map(t => (
                    <option key={t} value={t}>{t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                  ))}
                </select>
              </FormField>
              <FormField label="Status">
                <select name="status" className="form-select" defaultValue="scheduled">
                  {['scheduled', 'upcoming', 'in_progress', 'completed'].map(s => (
                    <option key={s} value={s}>{s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                  ))}
                </select>
              </FormField>
              <FormField label="Due Date">
                <input type="date" name="due_date" className="form-input" />
              </FormField>
              <FormField label="Description">
                <textarea name="description" rows={2} className="form-input" placeholder="Describe the maintenance work..." />
              </FormField>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Vendor">
                  <input type="text" name="vendor" className="form-input" placeholder="Service provider" />
                </FormField>
                <FormField label="Technician">
                  <input type="text" name="technician" className="form-input" placeholder="Technician name" />
                </FormField>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Cost ($)">
                  <input type="number" name="cost" step="0.01" min="0" className="form-input" />
                </FormField>
                <FormField label="Mileage at Service">
                  <input type="number" name="mileage_at_service" min="0" className="form-input" />
                </FormField>
              </div>
              <FormField label="Notes">
                <textarea name="notes" rows={2} className="form-input" placeholder="Additional notes..." />
              </FormField>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-sm rounded-lg" style={{ color: 'var(--muted-foreground)' }}>Cancel</button>
                <button type="submit" disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-foreground rounded-lg flex items-center gap-2"
                  style={{ background: '#10B981', opacity: saving ? 0.7 : 1 }}>
                  {saving && <Loader2 className="w-3 h-3 animate-spin" />}
                  Schedule
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, accent, warn }: {
  icon: React.ComponentType<{ className?: string }>; label: string; value: string | number; accent?: boolean; warn?: boolean;
}) {
  return (
    <div className="rounded-xl p-5" style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4" style={{ color: 'var(--muted-foreground)' }} />
        <span className="text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>{label}</span>
      </div>
      <span className="text-2xl font-bold" style={{ color: accent ? '#10B981' : warn ? '#EF4444' : 'var(--foreground)' }}>{value}</span>
    </div>
  );
}

function FormField({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1" style={{ color: 'var(--foreground)' }}>
        {label}{required && <span style={{ color: '#EF4444' }}> *</span>}
      </label>
      {children}
    </div>
  );
}
